import React, {useEffect, useState} from 'react';
import CustomModal from '../../../components/Modal/CustomModal';
import BuildingForm from '../SubComponents/BuildingForm';
import {UpdateBuildingModalApp} from '../Functionality/UpdateBuildingModalApp';
import TabPanel from '../../../components/ResourceAssignment/TabPanel';
import {AddBuildingResourceApp} from '../Functionality/AddBuildingResourceApp';
import {AssignedBuildingEmployeeApp} from '../Functionality/AssignedBuildingEmployeeApp';
import {BUILDING_RESIDENTS_TABS} from '../Model/Model';
import {EyeIcon} from "../../../components/buttons/IconButtons";
import {TABLES} from "../../../Apis/Tables";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 * @param {string} title - Modal title text
 * @param {boolean} show - Controls modal visibility
 * @param {Function} onHide - Closes the modal
 * @param {Object} building - Building being updated, taken from the list row
 * @param {boolean} modified - Indicates if the list data has changed
 * @param {Function} setModified - Flags the list to be reloaded after a successful update
 * @returns {JSX.Element} - React component for updating buildings
 * @constructor
 */

const UpdateBuildingModal = ({ title, show, onHide, building, modified, setModified }) => {
    const {
        t,
        item,
        canManageUsers,
        residents,
        setResidents,
        displayModal,
        setDisplayModal,
        handleChange,
        handleUpdateBuilding,
        handleCleanClick,
        emptyFields,
        getListStatus,
        employeeWorkStations,
        handleClose,
        showOverlay,
        overlayHiding,
        handleMouseEnter,
        handleMouseLeave,
        handleMapChange
    } = UpdateBuildingModalApp({
        show,
        onHide,
        building,
        modified,
        setModified
    });

    // Center of the map, it is kept on the coordinates the building was loaded
    // with so picking a new point on the map does not move the view
    const [mapCenter, setMapCenter] = useState(null);

    // Residents section rendered under the form, only on update: delete buffer of
    // the assigned tab and the shared search / refetch flags; the add tab selection
    // lives in UpdateBuildingModalApp, it is sent on confirm
    const [selectDeleteResidents, setSelectDeleteResidents] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [sectionModified, setSectionModified] = useState(false);

    useEffect(() => {
        if (mapCenter !== null || item?.latitude === '' || item?.longitude === '') return;

        setMapCenter([Number(item.latitude), Number(item.longitude)]);
    }, [item?.latitude, item?.longitude]);

    return (
        <>
            <CustomModal show={show}
                         size={"xl"}
                         display={displayModal}
                         onConfirm={handleUpdateBuilding}
                         title={title}
                         onHide={handleClose}
                         classNameBody="pt-2"
            >
                <BuildingForm
                    idPrefix={'ubm'}
                    item={item}
                    handleChange={handleChange}
                    emptyFields={emptyFields}
                    getListStatus={getListStatus}
                    employeeWorkStations={employeeWorkStations}
                    t={t}
                    handleCleanClick={handleCleanClick}
                    showOverlay={showOverlay}
                    overlayHiding={overlayHiding}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                    handleMapChange={handleMapChange}
                    latlng={mapCenter}
                    showMapHint={false}
                />

                <div className='row mb-3 border-radius-8px border-full-shades-300'>
                    <div className={"col-12 p-0"}>
                        <div className={"d-flex align-items-center p-3"}>
                            <div className={"mr-2"}><EyeIcon/></div>
                            <p className={"fs-16px ibl-font-w-600"}>{t("custom_access")}</p>
                        </div>

                        <div className='border-bottom-shades-300 mb-3 ' />
                    </div>
                    <div className='col-12'>
                        <TabPanel
                            resource={TABLES.BUILDING_USER}
                            tabsConfig={BUILDING_RESIDENTS_TABS}
                            selected={residents}
                            setSelected={setResidents}
                            selectForDelete={selectDeleteResidents}
                            setSelectForDelete={setSelectDeleteResidents}
                            searchElement={searchElement}
                            setSearchElement={setSearchElement}
                            modified={sectionModified}
                            setModified={setSectionModified}
                            id={building?.id}
                            deleteButtonId={'btn_ubm_delete_residents'}
                            useAddApp={AddBuildingResourceApp}
                            useAssignedApp={AssignedBuildingEmployeeApp}
                            setDisplayModal={setDisplayModal}
                            mainContainerClass={'border-radius-8px border-full-shades-300'}
                            /* Inside a modal the viewport-based height leaves the
                               list a few pixels tall, the section sizes to its content */
                            autoHeight={false}
                            /* Reading the building already shows who is assigned to it,
                               only adding and removing users needs the permission */
                            disabled={!canManageUsers}
                        />
                    </div>
                </div>
            </CustomModal>
        </>
    );
};

export default UpdateBuildingModal;
